'use client';
// Critères d'acceptation des échantillons — ISO 15189:2022 §7.2.6.
//
// L'écran tient deux choses côte à côte, et leur DIFFÉRENCE de nature est le
// cœur du sujet :
//
//  · à gauche, la GRILLE des motifs de refus. Elle se saisit, elle est
//    paramétrable, elle vaut pour tout le laboratoire (restreignable par
//    famille). Elle est courte : une dizaine de lignes couvrent la paillasse.
//
//  · à droite, les CONDITIONS PAR EXAMEN. Elles ne se saisissent PAS ici : ce
//    sont les champs du manuel de prélèvement, affichés en lecture seule. Le
//    catalogue évolue par le circuit de propositions et de diffusions
//    versionnées, et nulle part ailleurs. Un second écran d'écriture des fiches
//    finirait par dire autre chose que le premier.
//
// Ce que l'écran refuse de faire :
//
//  · Prétendre qu'un examen a des critères opposables quand le manuel ne donne
//    ni volume minimal ni délai d'acheminement. Il le DIT, et renvoie au manuel
//    pour le corriger — plutôt que d'afficher une ligne vide qui se lirait
//    « rien à signaler ».
//
//  · Décider à la place du biologiste. La décision par défaut d'un motif est un
//    rappel de ce qui a été décidé en réunion, pas un automatisme : elle reste
//    modifiable à chaque réception.
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useGed, userName } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import {
  I, card, inputStyle, EmptyState, FilterBar, RefISO,
  FieldError, useBusy, Modale } from '@/components/ui';
import { H1, lbl } from './ressources/commun';
import { RegistresLies } from './manuel/RegistresLies';
import { useFormulaire } from '@/components/Formulaire';
import { useUrlsModules } from '@/shared/urlsModulesClient';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
/** Le manuel de prélèvement est une AUTRE application : on l'atteint par son
 *  URL, jamais par la navigation interne. Son adresse vient du contexte des
 *  modules (résolue à l'exécution), comme dans `RevueRef.tsx`, pour que les
 *  deux écrans mènent au même endroit. */

/** `sens` (13/08/2026) : un critère dit soit ce que l'échantillon DOIT
 *  présenter, soit ce qui le fait rejeter. Absent des grilles antérieures —
 *  elles valent alors « refus », ce qu'elles étaient. */
type Motif = { code: string; libelle: string; precision: string; sens?: string; decision: string; familles: string[] };
type Champ = { cle: string; label: string; valeur: string };
type Examen = {
  code: string; libelle: string; famille: string;
  champs: Champ[]; stabilites: string[]; manquants: string[]; opposable: boolean;
};
type Decision = {
  ref: string; titre: string; statut: string;
  motifRefus: string; ficheCode: string | null;
  decisionEchantillon: string; prescripteurInforme: boolean | null;
  echantillonRef: string | null; dateDeclaration: string; detectePar: string;
};
type Dossier = {
  grille: { motifs: Motif[]; majPar: string; majLe: string } | null;
  /** Empreinte de la grille lue (14/10/2026) : renvoyée à l'enregistrement. */
  version?: string;
  decisions: { code: string; label: string }[];
  familles: string[];
  examens: Examen[];
  recentes: Decision[];
  compte: {
    motifs: number; examens: number; examensOpposables: number; examensIncomplets: number;
    decisions: number; refus: number; reserves: number; regularises: number;
  };
};

const COULEUR_DECISION: Record<string, { bg: string; fg: string }> = {
  refus: { bg: 'var(--red-50)', fg: 'var(--red-600)' },
  reserve: { bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  regularise: { bg: 'var(--green-50)', fg: 'var(--green-600)' },
};

/* ── La grille ────────────────────────────────────────────────────────────── */


/** UN motif, ouvert seul et modifiable sur place.
 *
 *  La grille ne s'éditait qu'EN ENTIER : corriger une faute de frappe sur un
 *  motif ouvrait les dix lignes, avec leurs codes, leurs familles et leurs
 *  boutons « Retirer ». C'est beaucoup de surface exposée pour changer un mot,
 *  et rien ne disait quelle ligne on était venu corriger.
 *
 *  Le CODE reste figé ici, et c'est le point important : il identifie le motif
 *  dans les décisions de réception déjà enregistrées et dans les
 *  non-conformités qui en découlent. Le renommer orphelinerait cet historique
 *  sans que rien ne le signale. Il se change dans la grille complète, où l'on
 *  voit ce qu'on déplace.
 *
 *  L'enregistrement renvoie la grille ENTIÈRE avec cette seule ligne
 *  remplacée : le moteur n'accepte pas la mise à jour d'un motif isolé, et
 *  inventer une route pour cela aurait fait deux chemins d'écriture pour un
 *  même objet — celui que le dépôt a déjà payé plusieurs fois. */
function MotifModale({ motif, tous, version, familles, decisions, modifiable, onFait, onClose }: {
  motif: Motif; tous: Motif[]; version?: string; familles: string[];
  decisions: { code: string; label: string }[];
  modifiable: boolean; onFait: () => void; onClose: () => void;
}) {
  const [m, setM] = useState<Motif>(() => ({ ...motif, familles: [...motif.familles] }));
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const maj = (k: keyof Motif, v: unknown) => setM((x) => ({ ...x, [k]: v }));

  const enregistrer = () => run('save', async () => {
    setErreur('');
    if (!m.libelle.trim()) { setErreur('L’intitulé du motif est requis.'); return; }
    const liste = tous.map((x) => (x.code === motif.code ? m : x));
    /* La grille ENTIÈRE part avec la version de `tous` (14/10/2026) : si un autre l'a révisée
       depuis, le serveur refuse (409) au lieu d'effacer sa révision. */
    const r = await fetchEcriture('/api/ged/ged/criteres', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motifs: liste, version }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Enregistrement refusé.');
      return;
    }
    onFait();
  });

  return (
    <Modale
      titre={motif.libelle || motif.code}
      sousTitre={`Code ${motif.code} — figé : il identifie ce motif dans les décisions de réception déjà enregistrées.`}
      onClose={onClose}
      largeur={640}
      pied={modifiable ? (
        <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'center' }}>
          <span style={{ flex: 1 }} />
          <Bouton taille="sm" onClick={onClose}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" onClick={enregistrer} disabled={estOccupe()}>
            <I n="check" s={13} />{estOccupe('save') ? 'Enregistrement…' : 'Enregistrer'}
          </Bouton>
        </div>
      ) : undefined}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div>
          <label style={lbl} htmlFor="mm-lib">Intitulé du motif</label>
          <input id="mm-lib" value={m.libelle} maxLength={160} disabled={!modifiable}
            onChange={(e) => maj('libelle', e.target.value)} style={{ ...inputStyle, width: '100%' }} />
        </div>
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
          <div>
            <label style={lbl} htmlFor="mm-sens"
              title="Un critère d’acceptation énonce ce que l’échantillon doit présenter ; un motif de refus énonce ce qui le fait rejeter.">
              Sens du critère
            </label>
            <select id="mm-sens" value={m.sens || 'refus'} disabled={!modifiable}
              onChange={(e) => maj('sens', e.target.value)} style={inputStyle}>
              <option value="acceptation">Ce qu’on exige</option>
              <option value="refus">Ce qu’on refuse</option>
            </select>
          </div>
          <div>
            <label style={lbl} htmlFor="mm-dec"
              title="Ce que le laboratoire applique par défaut. Modifiable à chaque réception : c’est le biologiste qui tranche.">
              Décision par défaut
            </label>
            <select id="mm-dec" value={m.decision} disabled={!modifiable}
              onChange={(e) => maj('decision', e.target.value)} style={inputStyle}>
              {decisions.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={lbl} htmlFor="mm-prec">Précision — à quoi le reconnaît-on ?</label>
          <textarea id="mm-prec" value={m.precision} maxLength={1200} rows={3} disabled={!modifiable}
            onChange={(e) => maj('precision', e.target.value)}
            style={{ ...inputStyle, width: '100%', height: 'auto', padding: '9px 12px', resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
        </div>
        <div>
          <label style={lbl}>Familles concernées <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(aucune cochée = toutes)</span></label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {familles.map((f) => {
              const actif = m.familles.includes(f);
              return (
                <Bouton variante={actif ? 'primaire' : 'contour'} taille="sm" key={f} type="button" disabled={!modifiable} onClick={() => maj('familles', actif ? m.familles.filter((x) => x !== f) : [...m.familles, f])}>
                  {f}
                </Bouton>
              );
            })}
          </div>
        </div>
        <FieldError>{erreur}</FieldError>
        {!modifiable && (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            La grille des motifs se révise par le responsable qualité — elle engage tout le laboratoire.
          </p>
        )}
      </div>
    </Modale>
  );
}

/** Édition de la grille. Une ligne par motif, ajoutée et retirée sur place :
 *  une grille de dix lignes ne mérite pas un formulaire modal par ligne, et
 *  chaque clic supplémentaire est une ligne qui ne sera pas saisie. */
function EditeurGrille({ motifs, version, familles, decisions, onFait, onAnnule }: {
  motifs: Motif[]; version?: string; familles: string[]; decisions: { code: string; label: string }[];
  onFait: () => void; onAnnule: () => void;
}) {
  const [liste, setListe] = useState<Motif[]>(() => motifs.map((m) => ({ ...m, familles: [...m.familles] })));
  /* La version de la grille LUE à l'ouverture, figée avec la liste (14/10/2026). */
  const [versionLue] = useState(version);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const maj = (i: number, k: keyof Motif, v: unknown) => setListe(
    (x) => x.map((m, j) => (j === i ? { ...m, [k]: v } : m)),
  );
  const ajouter = () => setListe((x) => [...x, {
    code: '', libelle: '', precision: '', sens: 'refus', decision: 'refus', familles: [],
  }]);
  const retirer = (i: number) => setListe((x) => x.filter((_, j) => j !== i));

  const enregistrer = () => run('save', async () => {
    setErreur('');
    const r = await fetchEcriture('/api/ged/ged/criteres', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motifs: liste, version: versionLue }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Enregistrement refusé.');
      return;
    }
    onFait();
  });

  return (
    <div style={{ ...card, display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Les motifs valent pour <strong>tout le laboratoire</strong>. Restreignez-en un à
        certaines familles seulement quand il n’a pas de sens ailleurs — « prélèvement
        coagulé » ne concerne pas une urine. Laissé vide, un motif s’applique partout.
      </p>

      {liste.map((m, i) => (
        <div key={i} style={{ display: 'grid', gap: 8, padding: '11px 12px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 210px', gap: 8 }}>
            <div>
              <label style={lbl} htmlFor={`m-code-${i}`}>Code</label>
              <input id={`m-code-${i}`} value={m.code} maxLength={40}
                onChange={(e) => maj(i, 'code', e.target.value)}
                placeholder="hemolyse" style={{ ...inputStyle, height: 32, fontSize: 12.5, fontFamily: 'var(--font-mono)' }} />
            </div>
            <div>
              <label style={lbl} htmlFor={`m-lib-${i}`}>Intitulé du motif</label>
              <input id={`m-lib-${i}`} value={m.libelle} maxLength={160}
                onChange={(e) => maj(i, 'libelle', e.target.value)}
                placeholder="Échantillon hémolysé" style={{ ...inputStyle, height: 32, fontSize: 12.5 }} />
            </div>
            <div>
              <label style={lbl} htmlFor={`m-sens-${i}`}
                title="Un critère d’acceptation énonce ce que l’échantillon doit présenter ; un motif de refus énonce ce qui le fait rejeter. Les deux se disent, et ne se déduisent pas l’un de l’autre.">
                Sens du critère
              </label>
              <select id={`m-sens-${i}`} value={m.sens || 'refus'} onChange={(e) => maj(i, 'sens', e.target.value)}
                style={{ ...inputStyle, height: 32, fontSize: 12.5 }}>
                <option value="acceptation">Ce qu’on exige</option>
                <option value="refus">Ce qu’on refuse</option>
              </select>
            </div>
            <div>
              <label style={lbl} htmlFor={`m-dec-${i}`}
                title="Ce que le laboratoire a décidé d’appliquer par défaut. Modifiable à chaque réception : c’est le biologiste qui tranche.">
                Décision par défaut
              </label>
              <select id={`m-dec-${i}`} value={m.decision} onChange={(e) => maj(i, 'decision', e.target.value)}
                style={{ ...inputStyle, height: 32, fontSize: 12.5 }}>
                {decisions.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={lbl} htmlFor={`m-prec-${i}`}>Précision — à quoi le reconnaît-on ?</label>
            <input id={`m-prec-${i}`} value={m.precision} maxLength={1200}
              onChange={(e) => maj(i, 'precision', e.target.value)}
              placeholder="Sérum rosé à rouge, indice d’hémolyse supérieur au seuil de l’automate."
              style={{ ...inputStyle, height: 32, fontSize: 12.5 }} />
          </div>
          <div>
            <label style={lbl}>Familles concernées <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(aucune cochée = toutes)</span></label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {familles.map((f) => {
                const actif = m.familles.includes(f);
                return (
                  <Bouton variante={actif ? 'primaire' : 'contour'} taille="sm" key={f} type="button" onClick={() => maj(i, 'familles', actif ? m.familles.filter((x) => x !== f) : [...m.familles, f])}>
                    {f}
                  </Bouton>
                );
              })}
            </div>
          </div>
          <div>
            <Bouton variante="danger" taille="sm" onClick={() => retirer(i)}>Retirer ce motif</Bouton>
          </div>
        </div>
      ))}

      <div>
        <Bouton taille="sm" onClick={ajouter}>
          <I n="plus" s={13} />Ajouter un motif
        </Bouton>
      </div>

      <FieldError>{erreur}</FieldError>
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer la grille', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

/* ── La décision de réception ─────────────────────────────────────────────── */

/** Enregistrer une décision de réception. Un seul envoi : la décision EST la
 *  non-conformité pré-analytique, avec son motif et son examen déjà remplis.
 *  Rien à ressaisir — c'est la condition pour que ce soit fait à 7 h 20 en
 *  pleine série plutôt que « plus tard ». */
function FormDecision({ motifs, decisions, examens, onFait, onAnnule }: {
  motifs: Motif[]; decisions: { code: string; label: string }[]; examens: Examen[];
  onFait: (ref: string) => void; onAnnule: () => void;
}) {
  // Adresse du catalogue résolue à l'exécution (posée par le layout).
  const { manuel: MANUEL_URL } = useUrlsModules();
  const [motif, setMotif] = useState('');
  const [decision, setDecision] = useState('');
  const [ficheCode, setFicheCode] = useState('');
  const [echantillonRef, setEchantillonRef] = useState('');
  const [description, setDescription] = useState('');
  const [correction, setCorrection] = useState('');
  const [prescripteurInforme, setPrescripteurInforme] = useState(false);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const m = motifs.find((x) => x.code === motif) || null;
  // Le choix d'examen est restreint aux familles du motif : proposer un examen
  // auquel le motif ne s'applique pas, c'est fabriquer un refus que le serveur
  // rejettera — autant ne pas le proposer.
  const examensPossibles = useMemo(() => (
    !m || !m.familles.length ? examens : examens.filter((e) => m.familles.includes(e.famille))
  ), [m, examens]);
  const exam = examensPossibles.find((e) => e.code === ficheCode) || null;

  const declaration = useMemo(() => [
    { id: 'd-motif', label: 'Motif constaté', valeur: motif, requis: true },
  ], [motif]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);

  const envoyer = () => run('save', async () => {
    if (!verifier()) return;
    setErreur('');
    const r = await fetchEcriture('/api/ged/ged/criteres/decision', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        motif, decision: decision || undefined, ficheCode: ficheCode || undefined,
        echantillonRef, description, correction, prescripteurInforme,
      }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErreur(j?.error?.message || 'Enregistrement refusé.'); return; }
    onFait(String(j?.nc?.ref || ''));
  });

  return (
    <div ref={refFormulaire} style={{ ...card, display: 'grid', gap: 12 }}>
      <Bandeau />
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Ce que vous enregistrez ici <strong>est</strong> une non-conformité pré-analytique : elle
        prend une référence, entre au registre des non-conformités et y suit le circuit normal.
        Il n’y a rien à redéclarer ailleurs.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={lbl} htmlFor="d-motif">Motif constaté</label>
          <select id="d-motif" value={motif} onChange={(e) => { setMotif(e.target.value); setDecision(''); setFicheCode(''); }}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
            <option value="">— choisir —</option>
            {motifs.map((x) => <option key={x.code} value={x.code}>{x.libelle}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl} htmlFor="d-decision"
            title="Pré-remplie avec ce que le laboratoire a décidé pour ce motif. Vous pouvez en décider autrement : c’est le biologiste qui tranche.">
            Décision prise
          </label>
          <select id="d-decision" value={decision || (m?.decision ?? '')} onChange={(e) => setDecision(e.target.value)}
            disabled={!m} style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
            {decisions.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
          </select>
        </div>
      </div>

      {m?.precision && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', padding: '8px 11px', lineHeight: 1.45 }}>
          {m.precision}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={lbl} htmlFor="d-fiche">Examen concerné <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(facultatif)</span></label>
          <select id="d-fiche" value={ficheCode} onChange={(e) => setFicheCode(e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
            <option value="">— non précisé —</option>
            {examensPossibles.map((e) => <option key={e.code} value={e.code}>{e.code} — {e.libelle}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl} htmlFor="d-ech">Identifiant de l’échantillon <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(facultatif)</span></label>
          <input id="d-ech" value={echantillonRef} maxLength={80} onChange={(e) => setEchantillonRef(e.target.value)}
            placeholder="N° de tube, code-barres…" style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
        </div>
      </div>

      {/* Les conditions que le manuel porte pour cet examen, rappelées telles
          quelles : c'est à elles qu'on compare l'échantillon reçu, et les
          chercher dans un autre onglet est ce qui fait qu'on ne les regarde pas. */}
      {exam && (
        <div style={{ fontSize: 12.5, color: 'var(--text-body)', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', padding: '9px 12px', lineHeight: 1.5 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <strong style={{ color: 'var(--text-strong)' }}>Ce que le manuel exige pour {exam.code}</strong>
            <span style={{ flex: 1 }} />
            {/* La fiche complète, à un clic : au moment de refuser un
                échantillon, on veut souvent relire le paragraphe entier, pas
                seulement les cinq champs rappelés ici. */}
            <a href={`${MANUEL_URL}/?code=${encodeURIComponent(exam.code)}`} target="_blank" rel="noreferrer"
              title={`Ouvrir la fiche ${exam.code} dans le manuel de prélèvement (nouvel onglet)`}
              style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)', textDecoration: 'none' }}>
              Fiche complète au manuel ↗
            </a>
          </div>
          <div style={{ marginTop: 4 }}>
            {exam.champs.map((c) => <div key={c.cle}>{c.label} : {c.valeur}</div>)}
            {exam.stabilites.length > 0 && <div>Stabilités : {exam.stabilites.join(' · ')}</div>}
            {exam.manquants.length > 0 && (
              <div style={{ marginTop: 4, color: 'var(--amber-600)', fontWeight: 700 }}>
                Non renseigné au manuel : {exam.manquants.join(', ')}.
              </div>
            )}
          </div>
        </div>
      )}

      <div>
        <label style={lbl} htmlFor="d-desc">Ce que vous avez constaté</label>
        <textarea id="d-desc" value={description} rows={2} onChange={(e) => setDescription(e.target.value)}
          placeholder="Écrivez comme vous le diriez. Le responsable qualité instruit la suite."
          style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
      </div>
      <div>
        <label style={lbl} htmlFor="d-corr">Ce qui a été fait dans l’immédiat <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(facultatif)</span></label>
        <textarea id="d-corr" value={correction} rows={2} onChange={(e) => setCorrection(e.target.value)}
          placeholder="Nouveau prélèvement demandé, service prévenu…"
          style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
      </div>

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--text-body)' }}>
        <input type="checkbox" checked={prescripteurInforme} onChange={(e) => setPrescripteurInforme(e.target.checked)} />
        Le prescripteur ou le service a été informé
      </label>

      <FieldError>{erreur}</FieldError>
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer la décision', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe() || !motif, onClick: () => void envoyer() }} />
    </div>
  );
}

/* ── L'écran ──────────────────────────────────────────────────────────────── */

export function Criteres() {
  // Adresse du catalogue résolue à l'exécution (posée par le layout).
  const { manuel: MANUEL_URL } = useUrlsModules();
  const { session, settings } = useGed();
  const isRQ = session.roles.includes('rq');
  const [d, setD] = useState<Dossier | null>(null);
  const [chargement, setChargement] = useState(true);
  const [tour, setTour] = useState(0);
  const [ongletExamens, setOngletExamens] = useState(false);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [editeGrille, setEditeGrille] = useState(false);
  /* Le motif ouvert seul (retour Ines du 17/08) : on clique la ligne, on lit,
     on corrige. La grille complète reste accessible par « Réviser la grille ».
     ⚠️ DÉCLARÉ ICI, avec les autres états, et non près de son usage : ce
     composant a DEUX retours anticipés plus bas (chargement, absence de
     dossier). Un hook posé après eux n'est appelé qu'à certains rendus —
     React lève alors « Rendered more hooks than during the previous render »
     et la barrière d'erreur remplace TOUTE l'application, pas seulement cet
     écran. C'est ce qui a fait disparaître les cartes de contrôle interne le
     17/08 : elles n'avaient rien perdu, l'application ne se rendait plus. */
  const [motifOuvert, setMotifOuvert] = useState<Motif | null>(null);
  const [saisieDecision, setSaisieDecision] = useState(false);
  const [confirmee, setConfirmee] = useState('');

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/criteres', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setD(j); })
      .catch(() => { if (vivant) setD(null); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);

  const recharger = () => { setEditeGrille(false); setSaisieDecision(false); setTour((t) => t + 1); };

  const examensFiltres = useMemo(() => {
    const base = (d?.examens || []).filter((e) => (
      filtre === 'incomplets' ? !e.opposable : filtre === 'opposables' ? e.opposable : true));
    const qq = q.trim().toLowerCase();
    return qq ? base.filter((e) => `${e.code} ${e.libelle} ${e.famille}`.toLowerCase().includes(qq)) : base;
  }, [d, filtre, q]);

  if (chargement) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement des critères…</p>;
  if (!d) return null;
  const { grille, compte, familles, decisions } = d;
  const motifs = grille?.motifs || [];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow={<>Pré-analytique · <RefISO code="7.2.6" /></>} title="Critères d’acceptation des échantillons" />
      <RegistresLies actif="criteres" />

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Le manuel de prélèvement dit à quelles <strong>conditions</strong> un examen se prélève.
        Cet écran porte leur pendant : à quelles conditions un échantillon reçu est
        <strong> refusé</strong>, et ce qu’on fait alors. Un refus — comme une acceptation sous
        réserve — <strong>est</strong> une non-conformité pré-analytique : elle s’enregistre ici
        et suit son circuit au registre des non-conformités, sans ressaisie.
      </p>

      {confirmee && (
        <div role="status" style={{ background: 'var(--green-50)', borderInlineStart: '3px solid var(--green-600)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)' }}>
          Décision enregistrée sous la référence{' '}
          <strong style={{ fontFamily: 'var(--font-mono)' }}>{confirmee}</strong> au registre des non-conformités.
        </div>
      )}

      {/* Grille non paramétrée : l'écran le DIT. Un écran de critères vide se
          lirait « le laboratoire ne refuse rien » — le pire contresens possible
          sur cette clause. */}
      {!grille && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-strong)' }}>Aucune grille de motifs de refus n’est paramétrée.</strong>{' '}
          Ce n’est pas « le laboratoire n’a pas de critère de refus » : c’est que rien n’a encore
          été posé. Tant que la grille est vide, aucune décision de réception ne peut être
          enregistrée — ce que le laboratoire refuse est sa décision, pas un réglage de l’outil.
        </div>
      )}

      {/* Onglets. Deux natures différentes, deux endroits — mais sur le même
          écran, parce que c'est leur rapprochement qui répond à la clause. */}
      <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid var(--border-default)' }}>
        {[
          { k: false, label: `Grille des motifs (${compte.motifs})` },
          { k: true, label: `Conditions par examen (${compte.examens})` },
        ].map((t) => (
          <button key={String(t.k)} onClick={() => setOngletExamens(t.k)} className="ged-btn"
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              padding: '7px 12px', fontSize: 12.5,
              fontWeight: ongletExamens === t.k ? 700 : 500,
              color: ongletExamens === t.k ? 'var(--color-primary-active)' : 'var(--text-muted)',
              borderBottom: ongletExamens === t.k ? '2px solid var(--color-primary-active)' : '2px solid transparent',
              marginBottom: -1,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {motifOuvert && (
        <MotifModale
          motif={motifOuvert}
          tous={motifs}
          version={d.version}
          familles={familles}
          decisions={decisions}
          modifiable={isRQ}
          onClose={() => setMotifOuvert(null)}
          onFait={() => { setMotifOuvert(null); recharger(); }}
        />
      )}

      {!ongletExamens && (
        <>
          {editeGrille && isRQ ? (
            <EditeurGrille motifs={motifs} version={d.version} familles={familles} decisions={decisions}
              onFait={recharger} onAnnule={() => setEditeGrille(false)} />
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Le journal du PARAMÉTRAGE, à côté du bouton qui le modifie : ces
                    réglages s'écrivaient au journal sous une clé fixe sans aucun
                    accès à l'écran (audit traçabilité du 24/08/2026). */}
                <BoutonJournal reference="CRITERES-ACCEPTATION" quoi="des révisions de la grille" />
                {isRQ && (
                  <Bouton taille="sm" onClick={() => setEditeGrille(true)}>
                    <I n="gear" s={13} />{motifs.length ? 'Réviser la grille' : 'Poser la grille'}
                  </Bouton>
                )}
                {motifs.length > 0 && !saisieDecision && (
                  <Bouton variante="primaire" taille="sm" onClick={() => { setSaisieDecision(true); setConfirmee(''); }}>
                    <I n="plus" s={13} />Enregistrer une décision de réception
                  </Bouton>
                )}
                <span style={{ flex: 1 }} />
                {grille?.majLe && (
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Grille revue le {fmtDate(grille.majLe)}
                    {grille.majPar ? ` par ${userName(settings, grille.majPar)}` : ''}
                  </span>
                )}
              </div>

              {saisieDecision && (
                <FormDecision motifs={motifs} decisions={decisions} examens={d.examens}
                  onFait={(ref) => { setConfirmee(ref); recharger(); }}
                  onAnnule={() => setSaisieDecision(false)} />
              )}

              {motifs.length === 0 ? (
                <EmptyState
                  titre="Aucun motif de refus n’est défini."
                  indice="La grille vaut pour tout le laboratoire et tient en une dizaine de lignes : tube non identifié, volume insuffisant, délai d’acheminement dépassé, contenant souillé… Ce sont les mêmes causes pour toute la paillasse — ce qui varie d’un examen à l’autre, c’est le seuil, et il est déjà dans le manuel."
                  action={isRQ ? (
                    <Bouton variante="primaire" onClick={() => setEditeGrille(true)}>
                      <I n="plus" s={14} />Poser la grille
                    </Bouton>
                  ) : undefined} />
              ) : (
                <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                  {motifs.map((m, i) => {
                    const c = COULEUR_DECISION[m.decision] || COULEUR_DECISION.refus;
                    return (
                      <div key={m.code} role="button" tabIndex={0}
                        onClick={() => setMotifOuvert(m)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMotifOuvert(m); } }}
                        title={isRQ ? 'Ouvrir ce motif — modifiable' : 'Ouvrir ce motif'}
                        className="ged-row"
                        style={{ padding: '11px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{m.code}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{m.libelle}</span>
                          {/* Le SENS d'abord : sans lui, « Identification
                              complète et concordante » s'affichait avec la
                              pastille « Échantillon refusé », ce qui se lisait
                              exactement à l'envers de ce que le critère dit. */}
                          <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px',
                            background: (m.sens || 'refus') === 'acceptation' ? 'var(--green-50)' : 'var(--red-50)',
                            color: (m.sens || 'refus') === 'acceptation' ? 'var(--green-600)' : 'var(--red-600)' }}>
                            {(m.sens || 'refus') === 'acceptation' ? 'Ce qu’on exige' : 'Ce qu’on refuse'}
                          </span>
                          {/* La décision ne se montre que sur un motif de REFUS :
                              sur une exigence, elle décrit ce qui arrive quand
                              elle n'est PAS satisfaite, et l'afficher à côté de
                              l'énoncé positif induisait en erreur. */}
                          {(m.sens || 'refus') !== 'acceptation' && (
                            <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: c.bg, color: c.fg }}>
                              {decisions.find((x) => x.code === m.decision)?.label || m.decision}
                            </span>
                          )}
                          <span style={{ flex: 1 }} />
                          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                            {m.familles.length ? m.familles.join(' · ') : 'toutes familles'}
                          </span>
                        </div>
                        {m.precision && (
                          <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 4 }}>{m.precision}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Ce que la grille a produit. Une grille sans usage est une
                  grille qu'on n'applique pas, et c'est ce qu'un auditeur
                  vérifie en second. */}
              {d.recentes.length > 0 && (
                <div style={{ display: 'grid', gap: 8 }}>
                  <h2 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>
                    Décisions de réception tracées ({compte.decisions})
                  </h2>
                  <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {compte.refus} refus · {compte.reserves} acceptations sous réserve · {compte.regularises} régularisations.
                    Chacune est une non-conformité du registre, avec sa référence et son circuit.
                  </p>
                  <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                    {d.recentes.map((r, i) => {
                      const c = COULEUR_DECISION[r.decisionEchantillon] || COULEUR_DECISION.refus;
                      return (
                        <div key={r.ref} style={{ padding: '9px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{r.ref}</span>
                          <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: c.bg, color: c.fg }}>
                            {decisions.find((x) => x.code === r.decisionEchantillon)?.label || r.decisionEchantillon}
                          </span>
                          <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                            {motifs.find((m) => m.code === r.motifRefus)?.libelle || r.motifRefus}
                          </span>
                          {r.ficheCode && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{r.ficheCode}</span>}
                          {r.echantillonRef && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>éch. {r.echantillonRef}</span>}
                          <span style={{ flex: 1 }} />
                          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                            {fmtDate(r.dateDeclaration)}{r.detectePar ? ` · ${userName(settings, r.detectePar)}` : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {ongletExamens && (
        <>
          <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--text-strong)' }}>Lecture seule.</strong>{' '}
            Ces conditions viennent du <strong>manuel de prélèvement</strong> et se modifient
            là-bas, par le circuit de propositions et de diffusions versionnées. Elles ne sont
            pas recopiées ici : elles y sont lues. Un second endroit où les écrire finirait par
            dire autre chose que le premier.
          </div>

          {compte.examensIncomplets > 0 && (
            <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
              <strong>{compte.examensIncomplets} examen{compte.examensIncomplets > 1 ? 's' : ''} sans critère opposable.</strong>{' '}
              Le manuel n’y donne ni volume minimal, ni délai d’acheminement, ni stabilité :
              un refus fondé sur ces motifs n’y serait adossé à rien. Cela se corrige dans le
              manuel, par une proposition de modification.
            </div>
          )}

          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher un examen, une famille…"
            chips={[
              { k: 'tous', label: 'Tous', n: compte.examens },
              { k: 'opposables', label: 'Critères opposables', n: compte.examensOpposables },
              { k: 'incomplets', label: 'Incomplets au manuel', n: compte.examensIncomplets },
            ]}
            actif={filtre} onChip={setFiltre}
            total={compte.examens} affiches={examensFiltres.length}
            onReset={() => { setQ(''); setFiltre('tous'); }} />

          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {examensFiltres.slice(0, 200).map((e, i) => (
              <div key={e.code} style={{ padding: '10px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{e.code}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{e.libelle}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{e.famille}</span>
                  <span style={{ flex: 1 }} />
                  {!e.opposable && (
                    <span title={`Non renseigné au manuel : ${e.manquants.join(', ')}.`}
                      style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--amber-50)', color: 'var(--amber-600)', cursor: 'help' }}>
                      critère incomplet
                    </span>
                  )}
                  {/* Le bandeau dit « ces conditions se modifient au manuel » —
                      encore fallait-il pouvoir y aller. Sans ce lien, corriger
                      un volume minimal demandait de rouvrir le manuel, de
                      chercher la fiche, et de retrouver l'examen à la main
                      (demande de la RQ du 13/08/2026). */}
                  <a href={`${MANUEL_URL}/?code=${encodeURIComponent(e.code)}`} target="_blank" rel="noreferrer"
                    title={`Ouvrir la fiche ${e.code} dans le manuel de prélèvement (nouvel onglet)`}
                    style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)', textDecoration: 'none' }}>
                    Fiche au manuel ↗
                  </a>
                </div>
                <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--text-body)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  {e.champs.map((c) => (
                    <span key={c.cle}><span style={{ color: 'var(--text-muted)' }}>{c.label} :</span> {c.valeur}</span>
                  ))}
                  {e.stabilites.length > 0 && (
                    <span><span style={{ color: 'var(--text-muted)' }}>Stabilités :</span> {e.stabilites.join(' · ')}</span>
                  )}
                </div>
                {e.manquants.length > 0 && (
                  <div style={{ marginTop: 3, fontSize: 12.5, color: 'var(--amber-600)' }}>
                    Non renseigné au manuel : {e.manquants.join(', ')}.
                  </div>
                )}
              </div>
            ))}
            {examensFiltres.length === 0 && (
              <div style={{ padding: 16 }}>
                <EmptyState titre="Aucun examen ne correspond à ces critères."
                  action={<Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); }}>Réinitialiser</Bouton>} />
              </div>
            )}
          </div>
          {examensFiltres.length > 200 && (
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
              {examensFiltres.length} examens correspondent ; les 200 premiers sont affichés. Affinez la recherche.
            </p>
          )}
        </>
      )}
    </div>
  );
}
